import * as d3 from "d3";
import React, { useEffect, useRef } from "react";
import { drawRect, drawArc, drawCircle, drawLine } from "../utils/drawPitchfunctions";
import { useTheme } from "../contexts/ThemeContext";
import { drawLargeModuleChemistrys } from "../utils/drawModuleChemistry";
import { ChemistryPlayerPositions, TeamChemistry } from "../types/IOption";

interface ModuleFitLargePitchProps {
  positionResults: ChemistryPlayerPositions[];
  teamChemistry: TeamChemistry[];
  systemFitScore?: number;
  newPlayerID?: number;
  newPlayerClubURL?: string;
  oldPlayerID?: number;
  oldPlayerClubURL?: string;
  isShowClubImg?: boolean;
}

const ModuleFitLargePitch: React.FC<ModuleFitLargePitchProps> = (pitchProps) => {
  const { theme } = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const createFootballPitch = () => {
    const container = document.getElementById("large-pitch-container")!;
    const pitchWidth: number = container.clientWidth;
    const pitchHeight: number = pitchWidth * 0.625; // Maintain a 5:8 aspect ratio
    const svg: d3.Selection<SVGSVGElement, unknown, any, undefined> = d3
      .select<SVGSVGElement, unknown>(svgRef.current!)
      .attr("width", pitchWidth)
      .attr("height", pitchHeight);
    const scale: number = pitchWidth / 800;
    svg.selectAll("*").remove();
    svg
      .append("rect")
      .attr("width", pitchWidth)
      .attr("height", pitchHeight)
      .attr("rx", 4)
      .attr("ry", 4)
      .style("fill", theme.chemistryPitchFill)
      .style("stroke", theme.NumberColor)
      .style("stroke-width", 1)
      .style("stroke-linecap", "round")
      .style("stroke-linejoin", "round");
    const xScale = d3.scaleLinear().domain([0, 105]).range([0, pitchWidth]);
    const yScale = d3.scaleLinear().domain([0, 68]).range([0, pitchHeight]);
    const pitchGroup: d3.Selection<SVGGElement, any, null, undefined> = svg
      .append<SVGGElement>("g")
      .attr("width", pitchWidth)
      .attr("height", pitchHeight);
    drawCircle(pitchGroup, pitchWidth / 2, pitchHeight / 2, 60 * scale, "none", 0.5, theme.NumberColor, 1);
    drawCircle(pitchGroup, xScale(105 / 2), yScale(68 / 2), 5 * scale, theme.NumberColor, 0.5);
    drawLine(pitchGroup, pitchWidth / 2, 0, pitchWidth / 2, pitchHeight, theme.NumberColor, 1, undefined, 0.5);
    drawRect(
      pitchGroup,
      xScale(0),
      yScale(25.16),
      xScale(6.3) - xScale(0),
      yScale(42.84) - yScale(25.16),
      theme.NumberColor,
      "none",
      1,
      0.5
    );
    drawRect(
      pitchGroup,
      xScale(0),
      yScale(12.92),
      xScale(16.8) - xScale(0),
      yScale(55.08) - yScale(12.92),
      theme.NumberColor,
      "none",
      1,
      0.5
    );
    drawArc(pitchGroup, 105 * scale, pitchHeight / 2, 50 * scale, Math.PI * 0.85, Math.PI - Math.PI * 0.85, theme.NumberColor, "none", 0.5);
    drawCircle(pitchGroup, pitchWidth / 2, pitchHeight / 2, 60 * scale, "none", 0.5, theme.NumberColor, 1);
    drawRect(
      pitchGroup,
      xScale(88.2),
      yScale(12.92),
      xScale(105) - xScale(88.2),
      yScale(55.08) - yScale(12.92),
      theme.NumberColor,
      "none",
      1,
      0.5
    );
    drawRect(
      pitchGroup,
      xScale(98.7),
      yScale(25.16),
      xScale(105) - xScale(98.7),
      yScale(42.84) - yScale(25.16),
      theme.NumberColor,
      "none",
      1,
      0.5
    );
    drawArc(
      pitchGroup,
      pitchWidth - 105 * scale,
      pitchHeight / 2,
      50 * scale,
      Math.PI * 1.15,
      Math.PI + Math.PI * 0.85,
      theme.NumberColor,
      "none",
      0.5
    );
    drawCircle(pitchGroup, xScale(10.5), yScale(68 / 2), 5 * scale, theme.NumberColor, 0.5);
    drawCircle(pitchGroup, xScale(94.5), yScale(68 / 2), 5 * scale, theme.NumberColor, 0.5);
    drawCircle(pitchGroup, xScale(94.5), yScale(68 / 2), 5 * scale, theme.NumberColor, 0.5);
    drawLargeModuleChemistrys(
      pitchGroup,
      xScale,
      yScale,
      pitchProps.positionResults!,
      pitchProps.teamChemistry!,
      theme,
      pitchProps.newPlayerID!,
      pitchProps.newPlayerClubURL!,
      pitchProps.oldPlayerID!,
      pitchProps.isShowClubImg!
    );
  };
  useEffect(() => {
    if (pitchProps?.positionResults?.length > 0) {
      createFootballPitch();
    }
  }, [theme, pitchProps?.positionResults]);

  return (
    <div id="large-pitch-container">
      {pitchProps?.systemFitScore ? (
        <div className="large-pitch-av-key">
          <div>
            AVERAGE KEY PARTNERSHIP SCORE
            <span>{pitchProps?.systemFitScore}</span>
          </div>
        </div>
      ) : null}
      <svg ref={svgRef}> id={"pitch-image"}</svg>
    </div>
  );
};

export default ModuleFitLargePitch;
